import React, {useState} from 'react';
import {Box, Divider, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Edit} from '@mui/icons-material';

import {ManageSuppliersService} from '@store/suppliers/services';

import {Sizes} from '@constants/sizes.constants';

import {AddWhite, Delete} from '@icons/index';

import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import ManageAvailabilityModal
  from '@screens/admin/suppliers/details/catalog/manageService/components/ManageAvailability.modal';
import MultiTextFieldComponent from '@components/inputs/multiTextField/MultiTextField.component';

interface AvailabilitiesFormProps {
  isCreation?: boolean;
  service: ManageSuppliersService;
  updateService: (data: ManageSuppliersService) => void;
}

const AvailabilitiesForm = (props: AvailabilitiesFormProps) => {
  const {isCreation, service, updateService} = props;

  const {t} = useTranslation();

  const [showAvailability, setShowAvailability] = useState<number | boolean>(false);

  return (
    <>
      <Stack py="20px" spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize={16} fontWeight="700">
            {t('suppliers.details.catalog.manage.availabilities')}
          </Typography>
          <IconButton aria-label="small-square-text" centerRipple={false} onClick={() => setShowAvailability(true)}>
            <AddWhite/>
          </IconButton>
        </Stack>
        <Stack spacing={4}>
          {service.availabilities?.map((availability, index) => (
            <Stack key={availability.id || `availability ${index}`} spacing={1}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <InputLabel sx={{width: 106}}>
                    {t('suppliers.details.catalog.manage.serviceValidity')}*
                  </InputLabel>
                  <Box flex={0.5}>
                    <DatePickerComponent
                      label={t('global.dateFrom')}
                      inputWidth={175}
                      value={availability.availableFrom}
                      readOnly/>
                  </Box>
                  {availability.availableTo ?
                   <Box flex={0.5}>
                    <DatePickerComponent
                        inputWidth={175}
                        label={t('global.dateTo')}
                        value={availability.availableTo}
                        readOnly/>
                  </Box>
                   : null}
                </Stack>
                {!!availability.availableZipCodes?.length && (
                  <MultiTextFieldComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('suppliers.details.catalog.manage.zipCodeHandled')}
                    values={availability.availableZipCodes || []}
                    readOnly/>
                )}
              </Stack>
              <Stack direction="row" alignSelf="flex-end">
                <IconButton onClick={() => setShowAvailability(index)}>
                  <Edit color="primary"/>
                </IconButton>
                {isCreation && (
                  <IconButton
                    onClick={() => updateService({availabilities: (service.availabilities || []).filter((a, aIndex) => aIndex !== index)})}>
                    <Delete/>
                  </IconButton>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
      {showAvailability !== false && (
        <ManageAvailabilityModal
          handleClose={() => setShowAvailability(false)}
          availability={typeof showAvailability === 'boolean' ? undefined : service.availabilities?.[showAvailability]}
          handleSaved={(availability) => {
            if (typeof showAvailability === 'boolean') {
              updateService({availabilities: (service.availabilities || []).concat(availability)});
            } else {
              updateService({
                availabilities: (service.availabilities || []).map((a, aIndex) =>
                  aIndex === showAvailability ? availability : a
                )
              });
            }
          }}/>
      )}
    </>
  );
};

export default AvailabilitiesForm;
